import { useCallback, useEffect } from 'react'

function getAnnouncerNode(): HTMLDivElement | null {
  if (typeof document === 'undefined') return null
  let el = document.getElementById('yum-a11y-announcer') as HTMLDivElement | null
  if (!el) {
    el = document.createElement('div')
    el.id = 'yum-a11y-announcer'
    el.setAttribute('aria-live', 'polite')
    el.setAttribute('role', 'status')
    el.style.position = 'absolute'
    el.style.width = '1px'
    el.style.height = '1px'
    el.style.padding = '0'
    el.style.margin = '-1px'
    el.style.overflow = 'hidden'
    el.style.clip = 'rect(0,0,0,0)'
    el.style.whiteSpace = 'nowrap'
    el.style.border = '0'
    document.body.appendChild(el)
  }
  return el
}

/**
 * Announce a message to assistive tech via a singleton aria-live region.
 * Plain function so class components can use it too.
 */
export function announce(message: string, politeness: 'polite' | 'assertive' = 'polite') {
  const el = getAnnouncerNode()
  if (!el) return
  el.setAttribute('aria-live', politeness)
  el.textContent = ''
  // Reassigning text after a tick forces SRs to re-announce the same string.
  window.setTimeout(() => {
    const node = document.getElementById('yum-a11y-announcer')
    if (node) node.textContent = message
  }, 50)
}

/**
 * Politely announce a message to assistive tech via an aria-live region.
 * Creates a singleton aria-live container on first use.
 *
 * Usage:
 *   const announce = useAnnouncer()
 *   announce('Entry saved')          // polite
 *   announce('Upload failed', 'assertive')
 */
export function useAnnouncer() {
  useEffect(() => {
    getAnnouncerNode()
  }, [])

  return useCallback((message: string, politeness: 'polite' | 'assertive' = 'polite') => {
    announce(message, politeness)
  }, [])
}
